import type { App } from 'vue'

import type { Router } from 'vue-router'

import { isProdEnv, SENTRY_DSN } from '@/config/sentry'

interface InitSentryOptions {
  app: App
  router?: Router
}

let hasRegisteredBreadcrumbHooks = false
let sentryModule: typeof import('@sentry/vue') | null = null

const tryGetUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem('userInfo') || '{}') as Record<string, any>
  } catch {
    return {}
  }
}

export const addSentryBreadcrumb = (breadcrumb: {
  category?: string
  message?: string
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
  data?: Record<string, unknown>
}) => {
  if (!isProdEnv()) return
  if (!sentryModule) return
  sentryModule.addBreadcrumb(breadcrumb)
}

export const captureSentryException = (error: unknown, extra?: Record<string, unknown>) => {
  if (!isProdEnv()) return
  if (!sentryModule) return
  sentryModule.captureException(error, extra ? { extra } : undefined)
}

export const initSentry = async ({ app, router }: InitSentryOptions) => {
  if (!isProdEnv()) return

  try {
    const Sentry = await import('@sentry/vue')
    sentryModule = Sentry

    Sentry.init({
      app,
      dsn: SENTRY_DSN,
      release: __SENTRY_RELEASE__ || undefined,
      dist: __SENTRY_DIST__ || undefined,
      sendDefaultPii: true,
      enabled: true,
      environment: import.meta.env.MODE
    })

    // 基础上下文（只放高价值、低敏信息；更敏感的交给 sendDefaultPii / 业务端显式 setUser）
    const userInfo = tryGetUserInfo()
    if (userInfo?.userCode) {
      Sentry.setUser({
        id: String(userInfo.userCode)
      })
    }
    Sentry.setTag('language', localStorage.getItem('language') ?? '')

    // app 启动 breadcrumb（方便定位“打开页面后第一件事”）
    addSentryBreadcrumb({
      category: 'app',
      message: 'app.init',
      level: 'info',
      data: {
        mode: import.meta.env.MODE
      }
    })

    // 路由跳转 breadcrumb（最常用也最有用）
    if (router && !hasRegisteredBreadcrumbHooks) {
      hasRegisteredBreadcrumbHooks = true
      router.afterEach((to, from) => {
        addSentryBreadcrumb({
          category: 'navigation',
          message: `${String(from.fullPath || '')} -> ${String(to.fullPath || '')}`,
          level: 'info',
          data: {
            from: from.fullPath,
            to: to.fullPath,
            fromName: String(from.name || ''),
            toName: String(to.name || ''),
            title: String(to.meta?.title || '')
          }
        })
      })
    }
  } catch (error) {
    // prod 环境 Sentry 失败不影响主流程
    void error
  }
}

